/**
 * @file useUnifiedView.ts
 * @description Real-time Firestore listener for the unified_operations_view collection.
 *              Falls back to mock data when MOCK_MODE is true.
 */

import { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { UnifiedRecord } from "../types";
import { MOCK_UNIFIED_RECORDS } from "../data/mockData";

const MOCK_MODE = import.meta.env.VITE_USE_EMULATORS !== "true" &&
  !import.meta.env.VITE_FIREBASE_PROJECT_ID?.includes("your-project");

interface UseUnifiedViewResult {
  records: UnifiedRecord[];
  loading: boolean;
  error: string | null;
  isMock: boolean;
}

export function useUnifiedView(): UseUnifiedViewResult {
  const [records, setRecords] = useState<UnifiedRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // In mock mode, return synthetic data immediately
    if (MOCK_MODE) {
      setRecords(MOCK_UNIFIED_RECORDS);
      setLoading(false);
      return;
    }

    // Subscribe to real-time Firestore updates
    const q = query(
      collection(db, "unified_operations_view"),
      orderBy("last_reconciled_at", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          ...(doc.data() as UnifiedRecord),
          patient_id: doc.id,
        }));
        setRecords(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("[useUnifiedView] Firestore error:", err);
        setError(`Failed to load unified view: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { records, loading, error, isMock: MOCK_MODE };
}
